import React, { useMemo } from 'react';

function ChevronIcon({ direction }) {
  const d = direction === 'prev' ? 'M12 16l-4-4 4-4M8 12h9' : 'M8 8l4 4-4 4M12 12H3';
  return (
    <svg
      className="catalog-pagination__icon"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d={d}
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Build page list with ellipsis for long catalogs, e.g. [1, '…', 4, 5, 6, '…', 12]. */
function buildPageItems(page, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const core = new Set([1, totalPages, page, page - 1, page + 1]);
  const sorted = [...core].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  const items = [];
  for (let i = 0; i < sorted.length; i += 1) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) items.push('…');
    items.push(sorted[i]);
  }
  return items;
}

export default function CatalogPagination({ page, totalPages, onPageChange }) {
  const items = useMemo(() => buildPageItems(page, totalPages), [page, totalPages]);

  if (totalPages <= 1) return null;

  const go = (next) => {
    const clamped = Math.min(totalPages, Math.max(1, next));
    if (clamped === page) return;
    onPageChange(clamped);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <nav className="catalog-pagination" aria-label="Product pages">
      <button
        type="button"
        className="catalog-pagination__nav"
        disabled={page <= 1}
        onClick={() => go(page - 1)}
        aria-label="Previous page"
      >
        <ChevronIcon direction="prev" />
        <span className="catalog-pagination__nav-label">Previous</span>
      </button>

      <div className="catalog-pagination__track" role="group" aria-label="Page numbers">
        {items.map((item, index) =>
          item === '…' ? (
            <span key={`gap-${index}`} className="catalog-pagination__gap" aria-hidden>
              …
            </span>
          ) : (
            <button
              key={`page-${item}`}
              type="button"
              className={`catalog-pagination__page${item === page ? ' catalog-pagination__page--active' : ''}`}
              onClick={() => go(item)}
              aria-label={`Page ${item}`}
              aria-current={item === page ? 'page' : undefined}
            >
              {item}
            </button>
          ),
        )}
      </div>

      <button
        type="button"
        className="catalog-pagination__nav"
        disabled={page >= totalPages}
        onClick={() => go(page + 1)}
        aria-label="Next page"
      >
        <span className="catalog-pagination__nav-label">Next</span>
        <ChevronIcon direction="next" />
      </button>
    </nav>
  );
}
